// ── El Dieciocho en MiPAE (17, 18 y 19 de septiembre) ────────────────────────
//
// Módulo puro y acotado: decide si estamos en Fiestas Patrias y devuelve el
// saludo de Benito y el chip de la fecha. Devuelve null cuando no aplica, así
// el llamador cae al comportamiento de siempre sin ramas extra (fail-safe
// hacia la app normal).
//
// El ángulo lo fijó el fundador: los usuarios de MiPAE TRABAJAN el 18. Estos
// textos saludan a quien está de turno sin hacerlo sentir que tiene que estar
// orgulloso de perderse la celebración: nada de "héroes", ni "vocación sin
// descanso", ni comparar a quien celebra con quien cuida. Escenas chicas
// (cambiar la hora del almuerzo, que te guarden algo en la casa) y punto.
//
// Tres reglas que se ganaron en la revisión editorial y no se relajan:
//
// 1. NO tener turno registrado NO significa estar libre. Cerrar la app tampoco
//    significa haber terminado de trabajar. Por eso "hoy tienes libre" (A2)
//    solo se dice con `libreHoy === true`, que el llamador afirma únicamente
//    cuando conoce la rotación del día. Sin esa certeza va el saludo neutro
//    (A0), que sirve para cualquiera. Misma disciplina que `TarjetaHoy`, que
//    con `turnoHoy = null` no afirma ningún turno ni "Día libre".
// 2. La fecha se lee en la zona horaria de la persona (getMonth/getDate son
//    locales a propósito). MiPAE tiene usuarios en 13 países: usar la app en
//    español no significa estar celebrando en Chile, y por eso el saludo
//    neutro NOMBRA a Chile en vez de darlo por hecho.
// 3. Benito sigue sin abrirse solo. Esto cambia lo que DICE cuando lo van a
//    buscar, nunca cuándo aparece. Cero notificaciones por la fecha.
//
// Lo que NO toca este módulo, por decisión explícita: nada de prosa festiva
// dentro de una evolución, una entrega de turno, un documento exportado ni una
// alerta clínica. El saludo vive en superficies conversacionales y de home.

import { hayTemaElegido } from './tema';

// Kill-switch ON por defecto, como todo flag nuevo del repo.
export function dieciochoHabilitado() {
  return process.env.REACT_APP_DIECIOCHO_ENABLED !== 'false';
}

// Septiembre es el mes 8 (base cero). El 17 entra porque la víspera es el
// único día en que el saludo puede adelantarse a quien mañana toma turno.
const DIAS = [17, 18, 19];

export function esDieciocho(fecha = new Date()) {
  if (!dieciochoHabilitado()) return false;
  const d = fecha instanceof Date ? fecha : new Date(fecha);
  if (Number.isNaN(d.getTime())) return false;
  return d.getMonth() === 8 && DIAS.includes(d.getDate());
}

// Los textos son los aprobados por el fundador el 2026-09-17 (tabla de
// selección: A1.1, A2.2, A3.1, A4.1, A0.1). No reescribirlos por estilo: el
// registro está calibrado (una sola referencia a comida por recorrido, cero
// bromas sobre alcohol, resaca, pacientes, errores ni alarmas).
export const SALUDOS = {
  // A1 · de turno el 18 o 19.
  turno: 'Hoy te toca turno y quería desearte unas felices Fiestas Patrias. Ojalá alcancen a compartir algo rico con el equipo, yo estoy aquí para ayudarte.',
  // A2 · con el día libre, y solo cuando de verdad lo sabemos.
  libre: 'Felices Fiestas Patrias! Hoy no te toca turno, así que ojalá puedas compartir algo rico, ver a tu gente o descansar un rato. Si necesitas algo, aquí estoy.',
  // A3 · saliendo del turno del 18. Solo el 18 y el 19: el texto nombra la
  // fecha, así que el 17 sería falso.
  saliendo: 'Terminaste el turno del 18, que tengas buen regreso y puedas descansar un rato. Yo me quedo por aquí, ojalá todavía te hayan guardado una empanada!',
  // A4 · el 17, sabiendo que mañana hay turno.
  vispera: 'Mañana te toca turno, así que te deseo desde hoy unas felices Fiestas Patrias. Ojalá alcances a compartir algo rico con tu gente, aquí estoy si necesitas ayuda.',
  // A0 · el que sirve siempre, y el que se usa ante cualquier duda.
  neutro: 'Estos días celebramos las Fiestas Patrias en Chile y quería mandarte un saludo. Estés de turno o con el día libre, aquí estoy para ayudarte con lo que necesites.',
};

/**
 * El saludo de la fecha, o null si hoy no es Dieciocho.
 *
 * @param {'antes'|'durante'|'cierre'|null} fase  fase del turno vigente.
 * @param {boolean|null} libreHoy   true SOLO si el llamador conoce la rotación
 *                                  y ese día no hay turno. null = no sabemos.
 * @param {boolean|null} turnoManana true SOLO si el llamador sabe que mañana
 *                                  hay turno (habilita la víspera del 17).
 */
export function saludoDieciocho({
  fase = null,
  libreHoy = null,
  turnoManana = null,
  fecha = new Date(),
} = {}) {
  if (!esDieciocho(fecha)) return null;
  const dia = (fecha instanceof Date ? fecha : new Date(fecha)).getDate();

  if (dia === 17) {
    // La víspera solo se afirma con el dato en la mano; si no, el neutro.
    return turnoManana === true ? SALUDOS.vispera : SALUDOS.neutro;
  }

  // 18 y 19.
  if (fase === 'cierre') return SALUDOS.saliendo;
  if (fase === 'durante' || fase === 'antes') return SALUDOS.turno;
  if (libreHoy === true) return SALUDOS.libre;
  return SALUDOS.neutro;
}

/**
 * El chip de la fecha, o null. Uno solo, según a quién le hablamos: quien está
 * de turno repasa su entrega, quien estudia repasa un caso. El `id` es lo único
 * que viaja a telemetría (el texto puede llevar contexto del usuario).
 */
export function chipDieciocho({ elegibleTurno = true, fecha = new Date() } = {}) {
  if (!esDieciocho(fecha)) return null;
  return elegibleTurno
    ? {
      id: 'dieciocho_entrega',
      t: '¿Me ayudas a repasar lo que conviene incluir en la entrega de turno de este feriado, sin usar datos de pacientes?',
      l: 'Entrega del feriado',
    }
    : {
      id: 'dieciocho_caso',
      t: '¿Podemos repasar un caso ficticio de enfermería ambientado en un turno de Fiestas Patrias?',
      l: 'Repasemos un caso',
    };
}

// ── El ambiente del Dieciocho ────────────────────────────────────────────────
// La primera versión dejó el tema como una opción más del selector, y el
// fundador fue claro mirando el preview: así no se ambienta nada, porque nadie
// va a buscarlo. Del 17 al 19 el tema se aplica SOLO.
//
// A quién: a quien nunca eligió tema a mano, que es casi todo el mundo. Quien
// eligió Café o Rosado conserva el suyo (su elección es una decisión, no un
// default que podamos pisar), y elegir cualquier tema, MiPAE incluido, es la
// forma de salirse del ambiente de la fecha.
//
// Qué NO cambia, por seguridad clínica: el tema solo mueve las variables de
// AMBIENTE (papel, superficies, bordes, tinta, acento). Los semáforos
// `clinical-*`, los colores de turno `shift-*`, la identidad por módulo
// `--module-*` y `system-*` quedan intactos, igual que con los otros nueve
// temas. El rojo de la bandera no entra en la paleta: es decoración.
export function temaDelDia(temaActual, fecha = new Date()) {
  if (!esDieciocho(fecha)) return temaActual;
  if (hayTemaElegido()) return temaActual;
  return 'dieciocho';
}
